"use client";

import { useEffect, useState } from "react";

type UrlListener = () => void;

type UrlChangeManager = {
  listeners: Set<UrlListener>;
  nativePushState: History["pushState"];
  nativeReplaceState: History["replaceState"];
  pushState: History["pushState"];
  replaceState: History["replaceState"];
  popstateHandler: () => void;
  notificationQueued: boolean;
  installed: boolean;
};

const URL_CHANGE_MANAGER_KEY = Symbol.for(
  "resimo-probleme.current-url-without-hash.manager",
);

type WindowWithUrlChangeManager = Window & {
  [URL_CHANGE_MANAGER_KEY]?: UrlChangeManager;
};

function getCurrentUrlWithoutHash() {
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

function getUrlChangeManager() {
  const managerWindow = window as WindowWithUrlChangeManager;
  const existingManager = managerWindow[URL_CHANGE_MANAGER_KEY];

  if (existingManager) {
    return existingManager;
  }

  const manager = {} as UrlChangeManager;

  const notifyListeners = () => {
    if (manager.notificationQueued) {
      return;
    }

    manager.notificationQueued = true;
    queueMicrotask(() => {
      manager.notificationQueued = false;

      for (const listener of manager.listeners) {
        listener();
      }
    });
  };

  manager.listeners = new Set();
  manager.nativePushState = window.history.pushState;
  manager.nativeReplaceState = window.history.replaceState;
  manager.pushState = function pushState(
    this: History,
    ...args: Parameters<History["pushState"]>
  ) {
    const result = manager.nativePushState.apply(this, args);
    notifyListeners();
    return result;
  };
  manager.replaceState = function replaceState(
    this: History,
    ...args: Parameters<History["replaceState"]>
  ) {
    const result = manager.nativeReplaceState.apply(this, args);
    notifyListeners();
    return result;
  };
  manager.popstateHandler = () => {
    for (const listener of manager.listeners) {
      listener();
    }
  };
  manager.notificationQueued = false;
  manager.installed = false;

  managerWindow[URL_CHANGE_MANAGER_KEY] = manager;
  return manager;
}

function installUrlChangeListeners(manager: UrlChangeManager) {
  if (manager.installed) {
    return;
  }

  // A module re-evaluation can see the wrapper from an older instance. Keep
  // the native function held by the window-owned manager rather than wrapping
  // that wrapper and making it call itself.
  if (window.history.pushState !== manager.pushState) {
    manager.nativePushState = window.history.pushState;
    window.history.pushState = manager.pushState;
  }

  if (window.history.replaceState !== manager.replaceState) {
    manager.nativeReplaceState = window.history.replaceState;
    window.history.replaceState = manager.replaceState;
  }

  window.addEventListener("popstate", manager.popstateHandler);
  manager.installed = true;
}

export function useCurrentUrlWithoutHash() {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let previousUrl = getCurrentUrlWithoutHash();
    const manager = getUrlChangeManager();

    const updateUrl = () => {
      const nextUrl = getCurrentUrlWithoutHash();
      if (nextUrl !== previousUrl) {
        previousUrl = nextUrl;
        setUrl(nextUrl);
      }
    };

    setUrl(previousUrl);

    manager.listeners.add(updateUrl);
    installUrlChangeListeners(manager);

    return () => {
      manager.listeners.delete(updateUrl);
    };
  }, []);

  return url;
}
